import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import classNames from "classnames";
import Heading from "../../elements/heading";
import IconLink from "../../elements/icon-link";
import Image from "../../elements/image";
import PrimaryButton from "../../elements/primary-button";
import PrimaryLink from "../../elements/primary-link";
import { sortByCoordinates } from "../../utils/sorting";
import BodyText from "../../elements/body-text";
import "./style.scss";

let isAutoScrolling = false;
let isManualScrolling = false;
let source;

const Hotspot = props => {
  const [items, setItems] = useState(() =>
    sortByCoordinates(props.items || [])
  );
  const [activeIndex, setActiveIndex] = useState(
    () => props.items && props.items.findIndex(item => item.active)
  );
  const [imageIsLoaded, setImageStatus] = useState(false);

  const imageContainer = useRef();
  const imageComponent = useRef();
  const infoBlockContainer = useRef();
  const pinItemsRef = useRef([]);

  const scrollBy = useCallback((element, value, duration, easingFunc, top) => {
    infoBlockContainer.current.className = infoBlockContainer.current.className.replace(
      /snap/,
      ""
    );
    isAutoScrolling = true;
    setTimeout(() => {
      isAutoScrolling = false;
    }, duration);

    let startTime;
    let startPos = top ? element.scrollTop : element.scrollLeft;
    let clientSize = top ? element.clientHeight : element.clientWidth;
    let maxScroll = top
      ? element.scrollHeight - clientSize
      : element.scrollWidth - clientSize;
    let scrollIntendedDestination = startPos + value;
    let scrollEndValue = Math.min(
      Math.max(scrollIntendedDestination, 0),
      maxScroll
    );
    const scroll = function (timestamp) {
      startTime = startTime || timestamp;
      const elapsed = timestamp - startTime;
      if (top)
        element.scrollTop =
          startPos +
          (scrollEndValue - startPos) * easingFunc(elapsed / duration);
      else
        element.scrollLeft =
          startPos +
          (scrollEndValue - startPos) * easingFunc(elapsed / duration);
      elapsed <= duration && window.requestAnimationFrame(scroll);
    };
    if (startPos !== scrollEndValue) window.requestAnimationFrame(scroll);
  }, []);

  const scrollToActivePin = useCallback(() => {
    if (imageContainer.current && activeIndex >= 0) {
      const activePinItem = imageContainer.current.querySelectorAll(
        ".hotspot-pin"
      )[activeIndex],
        screenWidth = document.querySelector("html").clientWidth,
        toLeft = activePinItem.offsetLeft,
        toLeftOfScreen = toLeft - imageContainer.current.scrollLeft;

      scrollBy(
        imageContainer.current,
        toLeftOfScreen - screenWidth / 2,
        1000,
        easeInOutCubic
      );
    }
  }, [activeIndex, scrollBy]);

  const scrollToActiveItem = useCallback(() => {
    if (activeIndex >= 0 && infoBlockContainer.current) {
      let activeItemElement = infoBlockContainer.current.querySelectorAll(
        ".item"
      )[activeIndex],
        toLeft = activeItemElement.offsetLeft,
        toLeftOfScreen = toLeft - infoBlockContainer.current.scrollLeft;
      scrollBy(
        infoBlockContainer.current,
        toLeftOfScreen,
        1000,
        easeInOutCubic
      );
    }
  }, [activeIndex, scrollBy]);

  const setPins = useCallback(
    (imageElement, pinButtonElements) => {
      const imageHeight = getClosestHeight(
        imageElement.clientWidth,
        props.image
      );
      const width = imageElement.clientWidth,
        height = imageHeight || imageElement.clientHeight;
      for (let index = 0; index < items.length; index++) {
        const item = items[index],
          left = (width * item.coordinates[1]) / 100 - 20,
          top = (height * item.coordinates[0]) / 100 - 20;

        let pin = pinButtonElements[index];
        pin.style.left = `${left}px`;
        pin.style.top = `${top}px`;
        pin.style.color =
          props.icon && props.icon.color ? props.icon.color : "#fff";
        pinButtonElements[index] = pin;
      }
      const carouselElement = imageElement.closest(".carousel");
      if (carouselElement) {
        carouselElement.querySelector(".prev").style.height = `${height}px`;
        carouselElement.querySelector(".next").style.height = `${height}px`;
      }
    },
    [props.icon, items]
  );

  const getClosestHeight = (parentWidth, mediaObject) => {
    let differences = mediaObject.imagesizes
      ? mediaObject.imagesizes
        .map(item => {
          let dif = parentWidth - item.width;
          return { dif: dif, item: item };
        })
        .sort((a, b) => a.dif - b.dif)
        .filter(item => {
          return item.dif <= 0;
        })
      : [];
    return differences.length
      ? differences[differences.length - 1].item.height
      : null;
  };

  const handleResize = useCallback(() => {
    setPins(imageComponent.current.refs.image, pinItemsRef.current);
    scrollToActivePin();
    scrollToActiveItem();
  }, [setPins, scrollToActiveItem, scrollToActivePin]);

  const scrollToImageContainer = useCallback(() => {
    if (imageContainer.current && activeIndex >= 0) {
      scrollBy(
        document.querySelector("html"),
        imageContainer.current.getBoundingClientRect().top,
        1000,
        easeInOutCubic,
        true
      );
    }
  }, [activeIndex, scrollBy]);

  const toggleItem = useCallback(
    (currentIndex, _source) => {
      source = _source;
      if (items[currentIndex].active) {
        setActiveIndex(undefined);
        items[currentIndex].active = false;
      } else {
        items.map((item, index) => (item.active = index === currentIndex));
        setActiveIndex(currentIndex);
        setItems(items);
      }
    },
    [items]
  );

  const handleScroll = useCallback(
    e => {
      e.persist();
      if (!isAutoScrolling) {
        addSnapClass(e);

        // Clear our timeout throughout the scroll
        window.clearTimeout(isManualScrolling);

        // Set a timeout to run after scrolling ends
        isManualScrolling = setTimeout(function () {
          toggleItem(
            Math.ceil(
              e.target.scrollLeft / document.querySelector("html").clientWidth
            ),
            "hotspot-container"
          );
        }, 100);
      }
    },
    [toggleItem]
  );

  // Add event listeners for resize and scroll
  useEffect(() => {
    global.addEventListener("resize", handleResize);
    return () => {
      global.removeEventListener("resize", handleResize, false);
    };
  }, [handleResize]);

  // Set Pins on mount
  useEffect(() => {
    if (imageIsLoaded) {
      setPins(imageComponent.current.refs.image, pinItemsRef.current);
    }
  }, [setPins, imageIsLoaded]);

  useEffect(() => {
    if (source === "image-container") {
      setTimeout(function () {
        scrollToActiveItem();
      }, 100);
      scrollToActivePin();
    }

    if (source === "hotspot-container") {
      setTimeout(function () {
        scrollToActivePin();
      }, 100);
      scrollToActiveItem();
    }
  }, [activeIndex, scrollToActiveItem, scrollToActivePin]);

  const onImageClick = () => {
    setActiveIndex(undefined);
    setItems([...items.map(item => ({ ...item, active: false }))]);
    source = "image-container";
  };

  const handleImageStatus = () => {
    setImageStatus(true);
  };

  const easeInOutCubic = t => {
    return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  };

  const addSnapClass = e => {
    if (!e.target.className.match(/snap/)) e.target.className += " snap";
  };

  const getButton = button => {
    let type =
      button.ctatype &&
      button.ctatype.fields &&
      button.ctatype.fields.phrase &&
      button.ctatype.fields.phrase.value;
    if (type === "primary-button") {
      return <PrimaryButton {...button} />;
    } else if (type === "primary-link") {
      return <PrimaryLink {...button} />;
    } else {
      // default type
      return <IconLink {...button} />;
    }
  };

  const infoBlock = (model, index, backgroundColor) => {
    return (
      <div
        key={index}
        className={"info-block"}
        style={{
          backgroundColor: backgroundColor
        }}
      >
        {model.overlineText && (
          <span key={index} className={"overline"}>
            {model.overlineText}
          </span>
        )}
        <Heading
          className="title show-on-scroll"
          typographyClass="type-h2"
          {...model.heading}
        />
        {model.subheading && (
          <Heading
            className="subtitle"
            typographyClass="type-h4"
            {...model.subheading}
          />
        )}
        <BodyText className={"body-text"} html={model.body} />
        {model.button != null && (
          <div className={"button-container"}>{getButton(model.button)}</div>
        )}
      </div>
    );
  };

  const hotspot = model => {
    return (
      <React.Fragment>
        {model.image && (
          <div ref={imageContainer} className={"image-container"}>
            <span onClick={() => onImageClick()}>
              <AnimatePresence>
                <motion.div
                  key={imageComponent}
                  className="media"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Image
                    ref={imageComponent}
                    handleLoad={() => handleImageStatus()}
                    {...{ ...model.image, className: "no-hover" }}
                  />
                </motion.div>
              </AnimatePresence>
            </span>
            {items &&
              items.length > 0 &&
              items.map((item, index) => {
                const hotspotPinClasses = classNames("hotspot-pin", {
                  active: item.active === true
                }),
                  margin = [
                    item.coordinates[0]/100 <= 0.5 ? 20 : -50,
                    0,
                    0,
                    item.coordinates[1]/100 <= 0.5 ? 0 : 20
                  ],
                  transform = [
                    item.coordinates[0]/100 <= 0.5 ? "0%" : "-100%",
                    item.coordinates[1]/100 <= 0.5 ? "0%" : "-100%"
                  ],
                  marginCss = `${margin[0]}px ${margin[1]}px ${margin[2]}px ${margin[3]}px`,
                  transformCss = `translate(${transform[1]},${transform[0]})`;
                return (
                  <span
                    key={index}
                    ref={el => (pinItemsRef.current[index] = el)}
                    className={hotspotPinClasses}
                    onClick={e => {
                      e.stopPropagation();
                      scrollToImageContainer();
                      toggleItem(index, "image-container");
                    }}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          items[index].active === true
                            ? model.icon && model.icon.active
                              ? model.icon.active
                              : '<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M14.59 8L12 10.59 9.41 8 8 9.41 10.59 12 8 14.59 9.41 16 12 13.41 14.59 16 16 14.59 13.41 12 16 9.41 14.59 8zM12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>'
                            : model.icon && model.icon.inactive
                              ? model.icon.inactive
                              : '<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>'
                      }}
                    />
                    <span
                      onClick={e => {
                        e.stopPropagation();
                      }}
                      className={"context item"}
                      style={{
                        margin: marginCss,
                        transform: transformCss
                      }}
                    >
                      {infoBlock(item, index, item.itemBackgroundColor)}
                    </span>
                  </span>
                );
              })}
          </div>
        )}

        <div
          ref={infoBlockContainer}
          className="infoblock-container"
          onScroll={event => handleScroll(event)}
        >
          {items &&
            items.length > 0 &&
            items.map((item, index) => {
              return (
                <div key={index} className={"item"}>
                  {infoBlock(item, index, item.itemBackgroundColor)}
                </div>
              );
            })}
        </div>
      </React.Fragment>
    );
  };

  return hotspot(props);
};

Hotspot.propTypes = {
  gridType: PropTypes.oneOf(["normal", "story"]),
  icon: PropTypes.object,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      overlineText: PropTypes.string,
      heading: PropTypes.object,
      subheading: PropTypes.object,
      button: PropTypes.string,
      body: PropTypes.string
    })
  )
};

export default Hotspot;
